// « Un retour ? » — le bouton toujours présent en bas à droite de l'application.
//
// Il ouvre un petit panneau où l'on dit ce qu'on comprend, ce qu'on ne comprend
// pas, ce qui coince ou ce qu'on aimerait : par écrit, ou à la voix (le micro
// reste ouvert tant qu'on parle, et le texte s'écrit au fil de la parole). La
// page d'où part le retour est jointe d'office : « ici, je ne comprends pas »
// suffit. Le retour arrive au back-office et par e-mail à l'exploitant (060).
import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api/client';
import { Button, IconButton, MicIcon } from '../components/ui';
import { appendDictated, useDictation } from '../voice/useDictation';
import './feedback.css';

type Kind = 'incompris' | 'bug' | 'idee' | 'bravo';

const KINDS: { id: Kind; icon: string; label: string }[] = [
  { id: 'incompris', icon: '🤔', label: 'Je ne comprends pas' },
  { id: 'bug', icon: '🐞', label: 'Ça ne marche pas' },
  { id: 'idee', icon: '💡', label: 'Une idée' },
  { id: 'bravo', icon: '❤️', label: 'J’aime' },
];

export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<Kind>('incompris');
  const [message, setMessage] = useState('');
  const [voiceUsed, setVoiceUsed] = useState(false);
  const [thanks, setThanks] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);
  const launcher = useRef<HTMLButtonElement>(null);

  const dictation = useDictation((text) => {
    setVoiceUsed(true);
    setMessage((m) => appendDictated(m, text));
  });

  const send = useMutation({
    mutationFn: () => api.post<{ id: string }>('/api/feedback', {
      kind, message, voice: voiceUsed, page: window.location.pathname,
    }),
    onSuccess: () => {
      setThanks(true);
      setMessage('');
      setVoiceUsed(false);
    },
  });

  const close = () => {
    if (dictation.listening) dictation.toggle();
    setOpen(false);
    setThanks(false);
    send.reset();
    launcher.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    area.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // `close` change à chaque rendu ; seul l'état ouvert compte ici.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      {open ? (
        <section className="fb-panel" role="dialog" aria-labelledby="fb-title">
          <div className="fb-head">
            <h2 className="fb-title" id="fb-title">Un retour sur la plateforme</h2>
            <IconButton icon="close" onClick={close} label="Fermer" />
          </div>

          {thanks ? (
            <div className="fb-thanks" role="status">
              <span className="fb-thanks-icon" aria-hidden="true">🙏</span>
              <p><strong>Merci, c’est bien arrivé.</strong> Chaque retour est lu et nous aide à améliorer Zygosight.</p>
              <div className="fb-actions">
                <Button variant="ghost" onClick={() => setThanks(false)}>Ajouter autre chose</Button>
                <Button variant="ghost" onClick={close}>Fermer</Button>
              </div>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (dictation.listening) dictation.toggle();
                if (message.trim()) send.mutate();
              }}
            >
              <div className="fb-kinds" role="radiogroup" aria-label="De quoi s’agit-il ?">
                {KINDS.map((k) => (
                  <button
                    key={k.id}
                    type="button"
                    role="radio"
                    aria-checked={kind === k.id}
                    className={`fb-kind${kind === k.id ? ' fb-kind-on' : ''}`}
                    onClick={() => setKind(k.id)}
                  >
                    <span aria-hidden="true">{k.icon}</span> {k.label}
                  </button>
                ))}
              </div>

              <label className="fb-label" htmlFor="fb-message">
                Dites-nous ce que vous comprenez, ce que vous ne comprenez pas, ce qui coince…
              </label>
              <textarea
                id="fb-message"
                ref={area}
                className="zy-input fb-text"
                rows={5}
                maxLength={5000}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={dictation.supported ? 'Écrivez ici, ou appuyez sur « Parler » et dites-le simplement.' : 'Écrivez ici.'}
              />

              {dictation.supported ? (
                <button
                  type="button"
                  className={`fb-talk${dictation.listening ? ' fb-talk-on' : ''}`}
                  onClick={dictation.toggle}
                  aria-pressed={dictation.listening}
                >
                  <MicIcon active={dictation.listening} />
                  {dictation.listening ? 'J’écoute… parlez librement, puis appuyez pour arrêter' : 'Parler'}
                </button>
              ) : null}
              {dictation.error ? <p className="zy-field__erreur">{dictation.error}</p> : null}
              {send.isError ? <p className="zy-field__erreur" role="alert">{send.error instanceof Error ? send.error.message : 'L’envoi a échoué.'}</p> : null}

              <div className="fb-actions">
                <span className="fb-note">La page où vous êtes est jointe à votre retour.</span>
                <Button type="submit" variant="accent" loading={send.isPending} disabled={!message.trim()}>
                  Envoyer
                </Button>
              </div>
            </form>
          )}
        </section>
      ) : null}

      <button
        ref={launcher}
        type="button"
        className="fb-launch"
        onClick={() => (open ? close() : setOpen(true))}
        aria-expanded={open}
      >
        <span aria-hidden="true">💬</span> Un retour ?
      </button>
    </>
  );
}
